import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Plus, Info, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const HOVER_DELAY_MS = 1500;
const PREVIEW_MS = 30000;

export default function MovieCard({ video, size = "md" }) {
  const sizes = {
    sm: "w-[150px] h-[85px]",
    md: "w-[220px] h-[125px] md:w-[260px] md:h-[145px]",
    lg: "w-[300px] h-[170px]",
  };
  const nav = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [muted, setMuted] = useState(true);
  const enterTimer = useRef(null);
  const stopTimer = useRef(null);

  const startHover = () => {
    setHovered(true);
    if (!video.trailer_yt) return;
    clearTimeout(enterTimer.current);
    enterTimer.current = setTimeout(() => {
      setPreviewing(true);
      stopTimer.current = setTimeout(() => setPreviewing(false), PREVIEW_MS);
    }, HOVER_DELAY_MS);
  };

  const endHover = () => {
    setHovered(false);
    setPreviewing(false);
    clearTimeout(enterTimer.current);
    clearTimeout(stopTimer.current);
  };

  useEffect(() => () => {
    clearTimeout(enterTimer.current);
    clearTimeout(stopTimer.current);
  }, []);

  const goDetail = () => nav(`/title/${video.id}`);
  const goPlay = (e) => { e.stopPropagation(); e.preventDefault(); nav(`/watch/${video.id}`); };
  const addList = async (e) => {
    e.stopPropagation(); e.preventDefault();
    try { await api.post(`/watchlist/${video.id}`); toast.success("Added to My List"); }
    catch { toast.error("Could not add"); }
  };

  return (
    <div
      data-testid={`movie-card-${video.id}`}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onClick={goDetail}
      className={`relative flex-shrink-0 snap-start cursor-pointer ${previewing ? "z-30" : "z-0"}`}
    >
      <div className={`${sizes[size]} relative rounded-md overflow-hidden bg-[#121212] transition-transform duration-300 ${hovered && !previewing ? "scale-105 shadow-2xl" : ""} ${previewing ? "scale-[1.15] shadow-[0_24px_60px_rgba(0,0,0,0.9)] ring-1 ring-white/10" : ""}`}>
        <img
          src={video.backdrop || video.poster}
          alt={video.title}
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity duration-500 ${previewing ? "opacity-0" : "opacity-100"}`}
        />
        {previewing && video.trailer_yt && (
          <iframe
            data-testid={`preview-iframe-${video.id}`}
            className="absolute inset-0 w-full h-full pointer-events-none"
            src={`https://www.youtube.com/embed/${video.trailer_yt}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&controls=0&playlist=${video.trailer_yt}&modestbranding=1&playsinline=1&rel=0&showinfo=0`}
            allow="autoplay; encrypted-media"
            title={`${video.title} preview`}
          />
        )}

        {/* Bottom gradient + meta */}
        <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent p-3 transition-opacity ${previewing ? "opacity-100 pt-10" : "opacity-90"}`}>
          <h4 className="text-white text-sm font-semibold line-clamp-1">{video.title}</h4>
          {previewing ? (
            <>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={goPlay} data-testid={`preview-play-${video.id}`} className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center hover:bg-gray-200" aria-label="play">
                  <Play className="w-4 h-4 fill-current ml-0.5" />
                </button>
                <button onClick={addList} data-testid={`preview-addlist-${video.id}`} className="w-8 h-8 rounded-full border-2 border-white/60 text-white flex items-center justify-center hover:border-white" aria-label="add to list">
                  <Plus className="w-4 h-4" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); goDetail(); }} className="w-8 h-8 rounded-full border-2 border-white/60 text-white flex items-center justify-center hover:border-white ml-auto" aria-label="more info">
                  <Info className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-[10px] mt-1.5">
                <span className="text-green-400 font-semibold">{Math.round((video.tmdb_rating || 7) * 10)}% Match</span>
                <span className="border border-white/30 px-1">{video.rating}</span>
                <span className="text-gray-300">{video.year}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-[11px] text-gray-300 mt-1 opacity-0 group-hover:opacity-100">
              <span className="text-green-400 font-semibold">{Math.round((video.tmdb_rating || 7) * 10)}% Match</span>
              <span>{video.year}</span>
              <span className="border border-white/30 px-1 text-[10px]">{video.rating}</span>
            </div>
          )}
        </div>

        {/* Type badge (only when not previewing) */}
        {hovered && !previewing && (
          <div className="absolute top-2 right-2">
            <span className="bg-[#E50914] text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wide">
              {video.type === "series" ? "Series" : "Movie"}
            </span>
          </div>
        )}

        {/* Mute toggle while previewing */}
        {previewing && (
          <button
            data-testid={`preview-mute-${video.id}`}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMuted((m) => !m); }}
            className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full border border-white/40 bg-black/60 flex items-center justify-center hover:bg-black/80"
            aria-label="toggle audio"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
